/**
 * Invoice TanStack Query Hooks (T092)
 * Data fetching and mutations for invoice management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient, { type ApiResponse } from '../lib/api-client';

// Types

export interface InvoiceLineItem {
  id?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount?: number;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  appointmentId?: string;
  subtotal: string;
  taxRate: string;
  tax: string;
  total: string;
  amountDue: number;
  status:
    | 'DRAFT'
    | 'SENT'
    | 'VIEWED'
    | 'PAID'
    | 'PARTIALLY_PAID'
    | 'OVERDUE'
    | 'CANCELLED'
    | 'REFUNDED';
  dueDate: string;
  paidAt?: string;
  sentAt?: string;
  viewedAt?: string;
  issueDate: string;
  notes?: string;
  createdAt: string;
  lineItems: InvoiceLineItem[];
  payments: Array<{ id: string; amount: string; status: string; paidAt: string }>;
  appointment?: { id: string; title: string; startTime: string } | null;
}

export interface CreateInvoiceInput {
  customerId: string;
  appointmentId?: string;
  dueDate: string;
  lineItems: Array<{ description: string; quantity: number; unitPrice: number }>;
  notes?: string;
}

export interface UpdateInvoiceInput {
  customerId?: string;
  appointmentId?: string;
  dueDate?: string;
  lineItems?: Array<{ description: string; quantity: number; unitPrice: number }>;
  notes?: string;
}

interface InvoicesResponse {
  invoices: Invoice[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Query Hooks

export function useInvoices(params?: { search?: string; status?: string; page?: number }) {
  return useQuery({
    queryKey: ['invoices', params?.search, params?.status, params?.page],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.search) {
        searchParams.append('search', params.search);
      }
      if (params?.status && params.status !== 'all') {
        searchParams.append('status', params.status);
      }
      if (params?.page) {
        searchParams.append('page', params.page.toString());
      }

      const response = await apiClient.get<ApiResponse<InvoicesResponse>>(
        `/invoices?${searchParams.toString()}`
      );
      return response.data.data;
    },
  });
}

export function useInvoice(invoiceId: string) {
  return useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Invoice>>(`/invoices/${invoiceId}`);
      return response.data.data;
    },
    enabled: !!invoiceId,
  });
}

// Mutation Hooks

export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInvoiceInput) => {
      const response = await apiClient.post<ApiResponse<Invoice>>('/invoices', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateInvoiceInput }) => {
      const response = await apiClient.put<ApiResponse<Invoice>>(`/invoices/${id}`, data);
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useSendInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const response = await apiClient.post<ApiResponse<Invoice>>(`/invoices/${invoiceId}/send`);
      return response.data.data;
    },
    onSuccess: (_data, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useVoidInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const response = await apiClient.post<ApiResponse<Invoice>>(`/invoices/${invoiceId}/void`);
      return response.data.data;
    },
    onSuccess: (_data, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useUpdateInvoiceStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Invoice['status'] }) => {
      const response = await apiClient.patch<ApiResponse<Invoice>>(`/invoices/${id}/status`, {
        status,
      });
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useDownloadInvoicePDF() {
  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const response = await apiClient.get(`/invoices/${invoiceId}/pdf`, {
        responseType: 'blob',
      });
      return { blob: response.data as Blob, invoiceId };
    },
    onSuccess: ({ blob, invoiceId }) => {
      downloadPDF(blob, `invoice-${invoiceId}.pdf`);
    },
  });
}

// Helper: trigger browser download from blob
function downloadPDF(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
